/**
 * Mengen ohne Protokolleintrag datieren.
 *
 * WOZU: kumulativ misst computeCumulativePositions gegen `quantityUsed`, mit
 * Stichtag aber nur gegen die bis dahin PROTOKOLLIERTE Menge. Was im
 * Leistungsverzeichnis steht, ohne dass ein Eintrag davon erzaehlt, gehoert zu
 * keinem Monat und faellt durch jedes Zeitfenster — dauerhaft. Bei einem
 * Bestandsprojekt mit externen Alt-Abschlaegen kippt das den Beleg in den
 * Blocker: die Abzuege werden IMMER in voller Hoehe abgezogen, die Grundlage
 * schrumpft aber auf das Protokollierte, und `payable_negative` schlaegt zu.
 *
 * Der Haken „Diesem Zeitraum zuschlagen" half nur diesem einen Beleg und
 * aenderte nichts. Hier wird die Ursache geschlossen: je Position ein
 * datierter Sammelnachtrag ueber die Luecke. Danach ist Eintragssumme =
 * quantityUsed, und jede weitere Zeitraumrechnung findet die Menge.
 *
 * Dasselbe Muster, das die Bestandsprojekte 2026-08 per SQL saniert hat — nur
 * aus der App, mit Sperrpruefung und ohne Umweg ueber den SQL-Editor.
 *
 * Rechenkern pur in invoiceLogic (undatedBacklog, planUndatedBacklogEntries);
 * hier steht nur, was Laden und Speichern betrifft.
 */
import type { Report, ReportMetadata, ServiceItem, ServiceTransaction } from '../types';
import { loadProjectById, saveProjectToCloud } from './cloudService';
import { getServiceLog, mirrorEntriesToJournal } from './serviceJournal';
import { getNormalizedServices } from './projectLv';
import { undatedBacklog, planUndatedBacklogEntries, UndatedBacklogItem } from './invoiceLogic';
import { fetchProjectLock } from './projectLockService';
import { projectWriteBlockForActor } from './invoiceService';

export interface UndatedBacklogResult {
    /** Wie viele Positionen einen Nachtrag bekommen haben. */
    count: number;
    /** Nettowert der nachgetragenen Menge. */
    netCents: number;
    positions: UndatedBacklogItem[];
}

/**
 * Traegt die Protokolluecke des Projekts zum angegebenen Datum nach.
 *
 * Wirft bei fremder Projektsperre — dieselbe Regel wie beim Ausstellen
 * (writeBackToProject): wer das Projekt offen hat, darf nicht ueberschrieben
 * werden, denn saveProjectToCloud schreibt bedingungslos.
 *
 * Nichts zu tun ist kein Fehler: count 0 heisst, das Protokoll ist bereits
 * vollstaendig.
 */
export const dateUndatedBacklog = async (
    projectId: string,
    opts: { date: string; actorId: string; actorName: string },
): Promise<UndatedBacklogResult> => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(opts.date)) {
        throw new Error('Für den Nachtrag fehlt ein gültiges Datum (JJJJ-MM-TT).');
    }

    const full = await loadProjectById(projectId);
    if (!full) throw new Error('Das Projekt wurde nicht gefunden.');

    const report = full.data as Report;
    const meta = report.metadata as ReportMetadata;
    const services: ServiceItem[] = meta.globalData?.services || getNormalizedServices(full);
    const log: ServiceTransaction[] = getServiceLog(report);

    const positions = undatedBacklog(services, log);
    if (positions.length === 0) return { count: 0, netCents: 0, positions: [] };

    // Erst fragen, dann schreiben — genau wie writeBackToProject. Fail-open:
    // ohne Sperre und ohne eigene Kennung wird nicht blockiert.
    const blocked = projectWriteBlockForActor(
        await fetchProjectLock(projectId),
        opts.actorId,
        Date.now(),
    );
    if (blocked) throw new Error(blocked);

    const entries = planUndatedBacklogEntries(positions, {
        date: opts.date,
        actorId: opts.actorId,
        actorName: opts.actorName,
        newId: () => crypto.randomUUID(),
    });

    const netCents = positions.reduce((sum, p) => sum + p.netCents, 0);
    // Der Vermerk gehoert ins Projekt, nicht nur ins Protokoll: er nennt die
    // Aussage, die hier getroffen wurde, samt Datum und Urheber.
    const note = {
        id: crypto.randomUUID(),
        date: new Date().toISOString(),
        author: opts.actorName || 'System',
        text: `${positions.length} Position(en) ohne Protokolleintrag auf den ${opts.date} datiert (Sammelnachtrag).`,
    };

    const nextReport = {
        ...report,
        metadata: {
            ...meta,
            globalData: {
                ...meta.globalData,
                // Die Mengen selbst bleiben unangetastet: quantityUsed ist die
                // Wahrheit, das Protokoll zieht nur nach. Wer hier zusaetzlich
                // die Menge erhoehte, verdoppelte den Leistungsstand.
                services,
                serviceLog: [...entries, ...log],
                notes: [note, ...(meta.globalData?.notes || [])],
            },
        },
    } as Report;

    await saveProjectToCloud(nextReport, projectId);

    // Zweitschrift ins Journal — erst nach dem BESTAETIGTEN Speichern. Der
    // Sammelnachtrag haengt nur an: `entries` sind genau die neuen Zeilen, der
    // Altbestand bleibt unberuehrt und geht deshalb auch nicht mit.
    await mirrorEntriesToJournal({
        projectId,
        entries,
        previousLog: log,
        source: 'undated_backlog',
        linkedProjectConfig: meta.globalData?.linkedProjectConfig ?? null,
    });

    return { count: positions.length, netCents, positions };
};
